<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[1, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton class="profile-button max-[600px]:hidden" preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor">
            <slot />
            <UserAvatar v-if="user" :user="user" size="30px" />
            <VaAvatar v-else :size="32" color="warning" src="https://admin.hitip.app/static/avatar/user.png" />
          </span>
        </VaButton>

        <div class="profile-mobile-anchor">
          <p class="text-gray-600 pr-2">Hi, {{ user?.first_name }}!</p>
          <VaIcon
            name="mso-expand_more"
            class="profile-mobile-anchor__chevron"
            :class="{ 'profile-mobile-anchor__chevron--open': isShown }"
            color="secondary"
          />
        </div>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="(group, groupIndex) in options" :key="groupIndex">
          <!-- <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ t(`user.${group.name}`) }}
          </header> -->
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="resolveLinkAttribute(item)"
            @click="(e?: Event) => onMenuItemClick(item, e)"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ t(`user.${item.name}`) }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors, useToast } from 'vuestic-ui'
import { useAuthStore } from '../../../../stores/auth-store'
import UserAvatar from '../../../../pages/users/widgets/UserAvatar.vue'
import { useRouter } from 'vue-router'

const authStore = useAuthStore()
const user = computed(() => authStore.user)

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { t } = useI18n()
const { push } = useRouter()
const { init } = useToast()

const TIPS_BASE_URL = (import.meta.env.VITE_TIPS_BASE_URL ?? 'https://tips.hitip.app').replace(/\/$/, '')

function moveToPaymentPage() {
  const id = user.value?.composite_id
  if (!id) {
    init({ message: 'Your tips link is not ready yet. Complete your profile.', color: 'warning' })
    return
  }
  const url = `${TIPS_BASE_URL}/qr/${id}`
  window.open(url, '_blank', 'noopener,noreferrer')
  isShown.value = false
}

function handleLogout() {
  authStore.logout()
  init({
    message: "You've successfully logged out",
    color: 'success',
  })
  push({ name: 'login' })
}

function onMenuItemClick(item: ProfileListItem, e?: Event) {
  // VaListItem often emits click without the native event — guard e
  if (item.action === 'tipsPage') {
    e?.preventDefault()
    moveToPaymentPage()
  } else if (item.action === 'logout') {
    e?.preventDefault()
    handleLogout()
  }
}

type ProfileListItem = {
  name: string
  to?: string
  href?: string
  icon: string
  /** Custom handler instead of route / href */
  action?: 'tipsPage' | 'logout'
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

withDefaults(
  defineProps<{
    options?: ProfileOptions[]
  }>(),
  {
    options: () => [
      {
        name: 'account',
        separator: true,
        list: [
          {
            name: 'profile',
            to: 'preferences',
            icon: 'mso-account_circle',
          },
          {
            name: 'faq',
            to: 'faq',
            icon: 'mso-quiz',
          },
          {
            name: 'helpAndSupport',
            href: 'mailto:info@hitip.app',
            icon: 'mso-error',
          },
        ],
      },
      {
        name: '',
        separator: false,
        list: [
          {
            name: 'yourPaymentPage',
            icon: 'mso-tab',
            action: 'tipsPage',
          },
          {
            name: 'logout',
            icon: 'mso-logout',
            action: 'logout',
          },
        ],
      },
    ],
  },
)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  if (item.action) return {}
  if (item.to) return { to: { name: item.to } }
  if (!item.href) return {}
  const isMailto = item.href.startsWith('mailto:')
  return isMailto ? { href: item.href } : { href: item.href, target: '_blank' }
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;
  display: flex;
  align-items: center;

  &__content {
    .menu-item {
      font-size: 14px;
    }
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
.profile-button {
  padding: 0px !important;
}

.profile-mobile-anchor {
  display: flex;
  align-items: center;
}

.profile-mobile-anchor__chevron {
  display: none;
  transition: transform 0.2s ease;
}

.profile-mobile-anchor__chevron--open {
  transform: rotate(180deg);
}

@media (max-width: 600px) {
  .profile-mobile-anchor__chevron {
    display: inline-flex;
  }
}
</style>
